import React, { useMemo } from "react";
import {
  aspectFromSize,
  thumbSizeFromAspect,
} from "../../../lib/toolPrepImageLayout";
import { useLiveSourceCardStats } from "../../../hooks/useLiveSourceCardStats";
import "../../../layout/resize/Editor/ResizeModeCards.css";
import "../../../shared/ToolPrepSourceCards.css";
import ToolPrepUploadDropzone from "../../../shared/Popup/ToolPrepUploadDropzone";
import {
  ToolPrepOriginalOverlays,
} from "../../../shared/ToolPrepSourceIdentity";
import "./OutpaintPresetCards.css";

const FALLBACK_ASPECT = 3 / 4;
const THUMB_WIDTH_PX = 140;

/**
 * - No image: single full-width upload (150px)
 * - With image: name → Original | Preview/Result → Replace | Remove
 *   Replace and Remove stay available after a result so the source can change.
 */
const OutpaintPresetCards = ({
  selectedKey = "preview",
  onSelect = () => {},
  originalImageUrl = null,
  previewImageUrl = null,
  naturalW = 0,
  naturalH = 0,
  expand = { left: 0, right: 0, up: 0, down: 0 },
  onUploadClick = null,
  onRemove = null,
  uploadProgress = null,
  sourceName = null,
  sourceSizeLabel = null,
  actionsDisabled = false,
}) => {
  const hasResult = Boolean(previewImageUrl);

  const aspect = useMemo(
    () => aspectFromSize(naturalW, naturalH, FALLBACK_ASPECT),
    [naturalW, naturalH]
  );
  const thumbSize = useMemo(
    () => thumbSizeFromAspect(aspect, THUMB_WIDTH_PX, FALLBACK_ASPECT),
    [aspect]
  );

  const previewLayout = useMemo(() => {
    if (!naturalW || !naturalH) {
      return {
        totalW: thumbSize.width,
        totalH: thumbSize.height,
        imgW: thumbSize.width,
        imgH: thumbSize.height,
        gL: 0,
        gR: 0,
        gU: 0,
        gD: 0,
      };
    }
    const outW = naturalW + (expand.left || 0) + (expand.right || 0);
    const outH = naturalH + (expand.up || 0) + (expand.down || 0);
    const fit = Math.min(thumbSize.width / outW, thumbSize.height / outH, 1);
    return {
      totalW: Math.max(1, Math.round(outW * fit)),
      totalH: Math.max(1, Math.round(outH * fit)),
      imgW: Math.max(1, Math.round(naturalW * fit)),
      imgH: Math.max(1, Math.round(naturalH * fit)),
      gL: Math.round((expand.left || 0) * fit),
      gR: Math.round((expand.right || 0) * fit),
      gU: Math.round((expand.up || 0) * fit),
      gD: Math.round((expand.down || 0) * fit),
    };
  }, [naturalW, naturalH, expand, thumbSize]);

  const { originalSizeFootnote, previewSizeFootnote } = useLiveSourceCardStats({
    originalImageUrl,
    previewImageUrl: previewImageUrl || originalImageUrl,
    originalSizeLabel: sourceSizeLabel,
    enabled: Boolean(originalImageUrl) && uploadProgress == null,
  });

  const thumbStyle = {
    width: thumbSize.width,
    height: thumbSize.height,
    aspectRatio: `${thumbSize.aspect}`,
  };

  if (uploadProgress != null) {
    return (
      <div className="tool-prep-source-cards tool-prep-source-cards--empty">
        <ToolPrepUploadDropzone kind="image" progress={uploadProgress} />
      </div>
    );
  }

  if (!originalImageUrl) {
    return (
      <div className="tool-prep-source-cards tool-prep-source-cards--empty">
        <ToolPrepUploadDropzone
          kind="image"
          onClick={() => onUploadClick?.()}
        />
      </div>
    );
  }

  return (
    <div className="tool-prep-source-cards tool-prep-source-cards--filled">
      <div className="resize-mode-grid tool-prep-source-cards__pair">
        <div
          className={`resize-mode-card tool-prep-source-cards__card${
            selectedKey === "original" ? " resize-mode-card--active" : ""
          }`}
        >
          <div className="tool-prep-source-cards__frame-wrap">
            <button
              type="button"
              className="tool-prep-source-cards__hit"
              onClick={() => onSelect("original")}
              aria-pressed={selectedKey === "original"}
              aria-label="Show original"
            >
              <div className="resize-mode-frame">
                <div className="resize-mode-thumb" style={thumbStyle}>
                  <img
                    src={originalImageUrl}
                    alt=""
                    className="resize-mode-thumb__img resize-mode-thumb__img--fallback"
                    draggable={false}
                  />
                </div>
              </div>
            </button>
            <ToolPrepOriginalOverlays
              onReplace={onUploadClick}
              onRemove={onRemove}
              disabled={actionsDisabled}
            />
          </div>
          <button
            type="button"
            className="tool-prep-source-cards__label-btn"
            onClick={() => onSelect("original")}
          >
            <span className="resize-mode-label">Original</span>
          </button>
          {originalSizeFootnote ? (
            <span className="tool-prep-source-cards__card-stat">
              {originalSizeFootnote}
            </span>
          ) : null}
        </div>

        <button
          type="button"
          className={`resize-mode-card${
            selectedKey === "preview" ? " resize-mode-card--active" : ""
          }`}
          onClick={() => onSelect("preview")}
          aria-pressed={selectedKey === "preview"}
        >
          <div className="resize-mode-frame">
            <div
              className="resize-mode-thumb otp-preset-thumb"
              style={thumbStyle}
            >
              {hasResult ? (
                <img
                  src={previewImageUrl}
                  alt=""
                  className="resize-mode-thumb__img resize-mode-thumb__img--fallback"
                  draggable={false}
                />
              ) : (
                <div
                  className="otp-preset-expand"
                  style={{
                    width: previewLayout.totalW,
                    height: previewLayout.totalH,
                  }}
                >
                  {previewLayout.gU > 0 ? (
                    <div
                      className="otp-preset-gutter"
                      style={{ height: previewLayout.gU }}
                    />
                  ) : null}
                  <div className="otp-preset-mid">
                    {previewLayout.gL > 0 ? (
                      <div
                        className="otp-preset-gutter"
                        style={{ width: previewLayout.gL }}
                      />
                    ) : null}
                    <img
                      src={originalImageUrl}
                      alt=""
                      className="otp-preset-img"
                      style={{
                        width: previewLayout.imgW,
                        height: previewLayout.imgH,
                      }}
                      draggable={false}
                    />
                    {previewLayout.gR > 0 ? (
                      <div
                        className="otp-preset-gutter"
                        style={{ width: previewLayout.gR }}
                      />
                    ) : null}
                  </div>
                  {previewLayout.gD > 0 ? (
                    <div
                      className="otp-preset-gutter"
                      style={{ height: previewLayout.gD }}
                    />
                  ) : null}
                </div>
              )}
            </div>
          </div>
          <span className="resize-mode-label">
            {hasResult ? "Result" : "Preview"}
          </span>
          {previewSizeFootnote ? (
            <span className="tool-prep-source-cards__card-stat">
              {previewSizeFootnote}
            </span>
          ) : null}
        </button>
      </div>
    </div>
  );
};

export default OutpaintPresetCards;
